import { ApplicationState } from 'application/redux';
import { addToaster } from 'core/toaster/redux/actions';
import { ToasterType } from 'core/toaster/redux/types';
import dayjs from 'dayjs';
import {
  DateDependency,
  MultipleSearchFilter,
  MULTIPLE_SEARCH_ADD_SELECTED_FILTERS,
  MULTIPLE_SEARCH_CLEAR_ALL_FILTERS,
  MULTIPLE_SEARCH_DELETE_FILTER,
  MULTIPLE_SEARCH_RESET_SHOW_FILTERS,
  MULTIPLE_SEARCH_RESET_STATE,
  MULTIPLE_SEARCH_SET_LIST_VALUES,
  MULTIPLE_SEARCH_TOGGLE_SHOW_FILTERS,
  MULTIPLE_SEARCH_UPDATE_INPUT
} from './types';

type DateRange = { start: string; end: string };

function isDateRange(value: any): value is DateRange {
  return !!value && typeof value === 'object' && 'start' in value && 'end' in value;
}

function getSelectedRange(searchItem: MultipleSearchFilter, name: string): DateRange | undefined {
  const filter = searchItem.selectedFilters.find((item) => item.name === name);
  return isDateRange(filter?.value) ? (filter?.value as unknown as DateRange) : undefined;
}

// True when the `after` range would start before the `before` range starts.
function violatesDateDependency(
  dependency: DateDependency,
  name: string,
  value: any,
  searchItem: MultipleSearchFilter
): boolean {
  let beforeRange: DateRange | undefined;
  let afterRange: DateRange | undefined;

  if (name === dependency.after && isDateRange(value)) {
    beforeRange = getSelectedRange(searchItem, dependency.before);
    afterRange = value;
  } else if (name === dependency.before && isDateRange(value)) {
    beforeRange = value;
    afterRange = getSelectedRange(searchItem, dependency.after);
  } else {
    return false;
  }

  if (!beforeRange || !afterRange) {
    return false;
  }

  return dayjs(afterRange.start).isBefore(beforeRange.start);
}

export function toggleShowMoreFilters(id: string) {
  return {
    type: MULTIPLE_SEARCH_TOGGLE_SHOW_FILTERS,
    payload: {
      id
    }
  };
}

export function updateInput(id: string, value: string, name: string) {
  return {
    type: MULTIPLE_SEARCH_UPDATE_INPUT,
    payload: {
      id,
      value,
      name
    }
  };
}

export function addSelectedFilters(id: string, value: string, name: string, type?: string) {
  return async (dispatch: any, getState: () => ApplicationState) => {
    const searchItem = getState().multipleSearch.multipleFilters.get(id);
    const hasItem =
      searchItem && searchItem?.selectedFilters.some((item: any) => item.name === name);

    if (searchItem && searchItem.selectedFilters.length >= 6 && !hasItem) {
      value = '';
      dispatch(
        addToaster({
          title: 'alert_info_messages.VIPOC_INFO_010',
          message: 'alert_info_messages.VIPOC_INFO_016',
          type: ToasterType.info,
        })
      );
      return dispatch(updateInput(id, '', name));
    }

    const breaksDateDependency = (searchItem?.dateDependencies || []).some((dependency) =>
      violatesDateDependency(dependency, name, value, searchItem!)
    );

    if (breaksDateDependency) {
      dispatch(
        addToaster({
          title: 'general.calendar',
          message: 'error_messages.VIPOC_ERROR_070',
          type: ToasterType.danger,
        })
      );
      return;
    }

    await dispatch({
      type: MULTIPLE_SEARCH_ADD_SELECTED_FILTERS,
      payload: {
        id,
        value,
        name,
        type
      }
    });
  };
}

export function deleteFilter(id: string, filter: any, index: number) {
  return {
    type: MULTIPLE_SEARCH_DELETE_FILTER,
    payload: {
      id,
      filter,
      index
    }
  };
}

export function clearAllFilters(id: string) {
  return {
    type: MULTIPLE_SEARCH_CLEAR_ALL_FILTERS,
    payload: {
      id
    }
  };
}

export function resetShowFilters(id: string) {
  return {
    type: MULTIPLE_SEARCH_RESET_SHOW_FILTERS,
    payload: {
      id
    }
  };
}

export function resetMultipleSearchState() {
  return {
    type: MULTIPLE_SEARCH_RESET_STATE
  };
}

export function setListValues(commandList: any) {
  return {
    type: MULTIPLE_SEARCH_SET_LIST_VALUES,
    payload: {
      commandList
    }
  };
}
